import { client } from '@/Prismic.js';
import { useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { getAllByFilter, getAllByTag, getAllByType, getByFilter, getSingle } from '@/utils/fetchers.js';
import { PVSProvider, isCorrectWhiteLabelDomain, isEternoDomain, isLocalEnvironment } from '@/utils/helpers.js';

import { useAdminConfigStore, useLocationStore, usePrismicStore } from '@/zustandStore.js';

import useAuth from './useAuth.ts';

const usePrismic = () => {
    const storedAppointmentTypes = usePrismicStore((state) => state.appointmentTypes);
    const storedInstanceConfigs = usePrismicStore((state) => state.instanceConfigs);
    const [shouldFetch, setShouldFetch] = useState(false);

    const isOutdatedInstanceConfigStructure = useMemo(() => {
        if (!storedInstanceConfigs) return false;

        const values = Object.values(storedInstanceConfigs);
        const hasUnsetProviderValues = values.flat().some((el) => !el.provider);
        return !Array.isArray(values[0]) || hasUnsetProviderValues;
    }, [storedInstanceConfigs]);

    const { data: adminScopesPage } = useSWRImmutable(
        shouldFetch ? ['config_admin_scopes', { lang: 'en-us' }] : null,
        getSingle
    );
    const { data: adminGroupsPage } = useSWRImmutable(
        shouldFetch ? ['config_admin_groups', { lang: 'en-us' }] : null,
        getSingle
    );
    const { data: todaysAppointmentsPage } = useSWRImmutable(
        shouldFetch ? ['todays_appointment_card', { lang: 'de-de' }] : null,
        getSingle
    );
    const { data: professionalPages } = useSWRImmutable(
        shouldFetch ? ['professional_profile', { lang: 'de-de' }] : null,
        getAllByType
    );
    const { data: instanceConfigs } = useSWRImmutable(
        shouldFetch || !storedInstanceConfigs || isOutdatedInstanceConfigStructure
            ? ['config_dc_instance', { lang: 'de-de' }]
            : null,
        getAllByType
    );
    const { data: customQuestions } = useSWRImmutable(
        shouldFetch ? ['custom_question', { lang: 'de-de' }] : null,
        getAllByType
    );
    const { data: appointmentTypes } = useSWRImmutable(
        shouldFetch || !storedAppointmentTypes ? ['service_or_treatment', { lang: 'de-de' }] : null,
        getAllByType
    );
    const { data: consentFormsTagDocuments } = useSWRImmutable(
        shouldFetch ? ['consent_forms_treatment', { lang: 'de-de' }] : null,
        getAllByTag
    );
    const { data: customerConfigDocuments } = useSWRImmutable(
        [
            {
                'my.customer_config.environment':
                    import.meta.env.VITE_ENVIRONMENT === 'local' ? 'dev' : import.meta.env.VITE_ENVIRONMENT,
            },
            {
                lang: 'de-de',
                pageSize: 100,
            },
        ],
        getAllByFilter
    );
    const { data: dataAssets } = useSWRImmutable(
        shouldFetch ? ['data_asset_dashboard', { lang: 'de-de' }] : null,
        getSingle
    );
    const { data: locationHubs } = useSWRImmutable(shouldFetch ? ['hub', { lang: 'de-de' }] : null, getAllByType);

    const { data: secondaryLocationMappings } = useSWRImmutable(
        ['secondary_location_mapping', { lang: 'de-de' }],
        getAllByType
    );
    const { data: secondaryLocations } = useSWRImmutable(['secondary_location', { lang: 'de-de' }], getAllByType);

    const { data: welcomeTreatmentContractPage } = useSWRImmutable(
        shouldFetch ? ['welcome_treatment_contract', { lang: 'de-de' }] : null,
        getSingle
    );

    const { data: accessRequestsPage } = useSWRImmutable(
        shouldFetch ? ['access_request', { lang: 'de-de' }] : null,
        getSingle
    );

    const { data: medicalRecords } = useSWRImmutable(
        shouldFetch ? ['medical_records', { lang: 'de-de' }] : null,
        getSingle
    );

    const { data: patientsAppConfig } = useSWRImmutable(
        [
            {
                'my.config_patient_app.environment':
                    import.meta.env.VITE_ENVIRONMENT === 'local' ? 'dev' : import.meta.env.VITE_ENVIRONMENT,
            },
            { lang: 'de-de' },
        ],
        getByFilter
    );

    const { user } = useAuth();
    const userGroups = usePrismicStore((state) => state.userGroups);
    const locations = useLocationStore((state) => state.locations);
    const timestamp = usePrismicStore((state) => state.timestamp);
    const prismicRef = usePrismicStore((state) => state.ref);
    const currentCustomer = usePrismicStore((state) => state.currentCustomer);
    const appVersion = usePrismicStore((state) => state.appVersion);

    const filterByEnvironmentTag = (prismicData) => {
        return prismicData.filter((entry) =>
            entry.tags.includes(import.meta.env.VITE_ENVIRONMENT === 'local' ? 'dev' : import.meta.env.VITE_ENVIRONMENT)
        );
    };

    useEffect(() => {
        const validateStore = async () => {
            if (!user) return;

            const now = Date.now();
            const isVersionMismatch = appVersion !== import.meta.env.VITE_VERSION;
            const timeElapsed = !timestamp || (now - parseInt(timestamp, 10)) / 60000 > 30;

            if (timeElapsed || isVersionMismatch) {
                const newStatePartial = {
                    timestamp: now,
                };

                if (isVersionMismatch) {
                    usePrismicStore.getState().resetStore();
                    newStatePartial.appVersion = import.meta.env.VITE_VERSION;
                }

                const { ref } = await client.getMasterRef();
                if (isVersionMismatch || ref !== prismicRef) {
                    newStatePartial.ref = ref;
                    setShouldFetch(true);
                }

                usePrismicStore.setState(newStatePartial);
            }
        };

        validateStore();
    }, [user]);

    useEffect(() => {
        if (adminScopesPage?.data?.scopes) {
            usePrismicStore.setState({ userScopes: adminScopesPage?.data?.scopes });
        }
    }, [adminScopesPage]);

    useEffect(() => {
        if (!locationHubs) return;
        usePrismicStore.setState({
            locationHubs: filterByEnvironmentTag(locationHubs),
        });
    }, [locationHubs]);

    useEffect(() => {
        if (todaysAppointmentsPage?.data) {
            usePrismicStore.setState({ todaysAppointmentsPrismicData: todaysAppointmentsPage.data });
        }
    }, [todaysAppointmentsPage]);

    useEffect(() => {
        if (adminGroupsPage?.data?.groups) {
            const groups = adminGroupsPage?.data?.groups.reduce((obj, group) => {
                if (
                    (import.meta.env.VITE_ENVIRONMENT === 'dev' && group.group[0].text.startsWith('prod-')) ||
                    (import.meta.env.VITE_ENVIRONMENT === 'prod' && group.group[0].text.startsWith('dev-'))
                ) {
                    return obj;
                }

                // eslint-disable-next-line no-param-reassign
                if (!obj[group.group[0].text]) obj[group.group[0].text] = [];
                if (group.app_component_key[0]?.text) {
                    obj[group.group[0].text].push(group.app_component_key[0].text);
                }
                return obj;
            }, {});

            usePrismicStore.setState({ userGroups: groups });
        }
    }, [adminGroupsPage]);

    useEffect(() => {
        if (!userGroups || !user || locations === undefined) return;

        const renderedPages = {};
        const featureAccess = {};

        user.groups?.forEach((group) => {
            userGroups[group]?.forEach((key) => {
                if (locations.length === 0 && key === 'eterno-today-appointments') return;
                if (!key.startsWith('feature') && !renderedPages[key]) renderedPages[key] = key;
                if (key.startsWith('feature') && !featureAccess[key]) featureAccess[key] = key;
            });
        });

        useAdminConfigStore.setState({ renderedPages: renderedPages, featureAccess: featureAccess });
    }, [adminGroupsPage, userGroups, locations, user]);

    useEffect(() => {
        if (!professionalPages) return;

        const filteredProfessionals = filterByEnvironmentTag(professionalPages);

        const professionals = filteredProfessionals.reduce((obj, professional) => {
            // eslint-disable-next-line no-param-reassign
            obj[professional.data.key[0].text] = professional.data;
            return obj;
        }, {});

        usePrismicStore.setState({ professionals: professionals });
    }, [professionalPages]);

    useEffect(() => {
        if (!instanceConfigs) return;

        const filteredConfigs = filterByEnvironmentTag(instanceConfigs);

        const configs = filteredConfigs.reduce((obj, config) => {
            // filter out instances without an instance_id
            if (!config?.data?.instance_id?.[0]?.text) return obj;

            const hubId = config.data.hub.id;
            const configData = {
                ...config.data,
                provider: config.data.provider ?? PVSProvider.DOC_CIRRUS,
            };

            // eslint-disable-next-line no-param-reassign
            if (!obj[hubId]) obj[hubId] = [];
            obj[hubId].push(configData);
            return obj;
        }, {});

        usePrismicStore.setState({ instanceConfigs: configs });
    }, [instanceConfigs]);

    useEffect(() => {
        if (!customQuestions) return;

        const { questionsObj, subQuestionsObj, allQuestionsObj } = customQuestions.reduce(
            (obj, question) => {
                // eslint-disable-next-line no-param-reassign
                obj.allQuestionsObj[question.id] = {
                    ...question.data,
                    prismic_id: question.id,
                };
                if (!question.data.question_key?.[0] || !question.data.title?.[0]) return obj;

                const key = question.tags.includes('subquestion') ? 'subQuestionsObj' : 'questionsObj';

                // eslint-disable-next-line no-param-reassign
                obj[key][question.data.question_key[0].text] = question.data;

                return obj;
            },
            { questionsObj: {}, subQuestionsObj: {}, allQuestionsObj: {} }
        );

        usePrismicStore.setState({
            questions: questionsObj,
            subQuestions: subQuestionsObj,
            allQuestions: allQuestionsObj,
        });
    }, [customQuestions]);

    useEffect(() => {
        if (!appointmentTypes) return;

        const filteredAppointmentTypes = filterByEnvironmentTag(appointmentTypes);

        const appointmentTypesObj = filteredAppointmentTypes.reduce((obj, appointmentType) => {
            if (!appointmentType.data.key?.[0]?.text) return obj;

            // eslint-disable-next-line no-param-reassign
            obj[appointmentType.data.key[0].text] = appointmentType.data;
            return obj;
        }, {});

        usePrismicStore.setState({ appointmentTypes: appointmentTypesObj });
    }, [appointmentTypes]);

    useEffect(() => {
        if (!consentFormsTagDocuments) return;
        usePrismicStore.setState({ consentFormDocuments: consentFormsTagDocuments });
    }, [consentFormsTagDocuments]);

    useEffect(() => {
        if (!dataAssets) return;
        usePrismicStore.setState({ labels: dataAssets });
    }, [dataAssets]);

    useEffect(() => {
        if (!customerConfigDocuments || !user || !storedInstanceConfigs || isOutdatedInstanceConfigStructure) return;

        const customerConfigDocumentResults = customerConfigDocuments.results;

        let filteredCustomerConfigDocuments =
            !isEternoDomain() && !isLocalEnvironment()
                ? customerConfigDocumentResults.filter(
                      (entry) => !entry.data.is_eterno_customer && isCorrectWhiteLabelDomain(entry.data.domain)
                  )
                : customerConfigDocumentResults.filter((entry) => entry.data.is_eterno_customer);

        filteredCustomerConfigDocuments = filteredCustomerConfigDocuments.filter(
            (entry) => entry?.data?.locations && entry.data.locations.length > 0
        );

        const customerConfigsObj = filteredCustomerConfigDocuments.reduce((obj, customerConfig) => {
            if (!customerConfig.data?.key?.[0]?.text) return obj;

            // eslint-disable-next-line no-param-reassign
            obj[customerConfig.data?.key?.[0]?.text] = { ...customerConfig.data, id: customerConfig.id };

            return obj;
        }, {});

        const allCustomers = customerConfigDocumentResults.reduce((obj, customerConfig) => {
            if (!customerConfig.data?.key?.[0]?.text) return obj;

            const instanceIds = customerConfig.data.locations.reduce((accumulator, location) => {
                if (!location?.location?.id || !storedInstanceConfigs[location.location.id]) return accumulator;
                const configs = storedInstanceConfigs[location.location.id];
                if (!configs) return accumulator;

                for (const config of configs) {
                    const instanceId = config?.instance_id?.[0]?.text;
                    if (!instanceId) continue;

                    // eslint-disable-next-line no-param-reassign
                    accumulator[instanceId] = true;
                }

                return accumulator;
            }, {});

            // eslint-disable-next-line no-param-reassign
            obj[customerConfig.data?.key?.[0]?.text] = {
                ...customerConfig.data,
                instanceIds: instanceIds,
                id: customerConfig.id,
            };

            return obj;
        }, {});

        usePrismicStore.setState({ allCustomers: allCustomers });
        useAdminConfigStore.setState({ customers: customerConfigsObj });

        // Guard the [0] accesses: a customer_config can be under-provisioned (no customer_id, no key),
        // and `currentCustomer` is rehydrated from persisted storage on reload — without the optional
        // chaining on [0], an empty customer_id/key array throws "reading 'text'" and white-screens.
        // Pick the admin default from customerConfigsObj (the "usable" set — already filtered to docs that
        // have a key) instead of the raw filtered list, so a keyless doc can't become currentCustomer.
        const usableCustomers = Object.values(customerConfigsObj);
        const firstCustomer = user?.groups?.includes('admin')
            ? usableCustomers[0]
            : usableCustomers.find(({ customer_id }) => user?.customerIds.includes(customer_id?.[0]?.text));

        usePrismicStore.setState({
            currentCustomer:
                user?.customerIds.includes(currentCustomer?.customer_id?.[0]?.text) &&
                customerConfigsObj[currentCustomer?.key?.[0]?.text]
                    ? currentCustomer
                    : firstCustomer,
        });
    }, [customerConfigDocuments, user, storedInstanceConfigs, isOutdatedInstanceConfigStructure]);

    useEffect(() => {
        if (!secondaryLocationMappings || !secondaryLocations) return;
        const filteredMappings = filterByEnvironmentTag(secondaryLocationMappings);
        const filteredSecondary = filterByEnvironmentTag(secondaryLocations);

        const secondaryLocationsFilteredReduced = filteredSecondary.reduce((obj, loc) => {
            // eslint-disable-next-line no-param-reassign
            obj[loc.id] = loc.data;
            return obj;
        }, {});

        const secondaryLocationMappingsFilteredReduced = filteredMappings.reduce((obj, mapping) => {
            const map = mapping.data.secondary_locations.map((val) => {
                return secondaryLocationsFilteredReduced[val.secondary_location.id];
            });
            // eslint-disable-next-line no-param-reassign
            obj[mapping.id] = map;
            return obj;
        }, {});
        useLocationStore.setState({ secondaryLocations: secondaryLocationMappingsFilteredReduced });
    }, [secondaryLocationMappings, secondaryLocations]);

    useEffect(() => {
        if (!welcomeTreatmentContractPage) return;

        usePrismicStore.setState({ welcomeTreatmentContract: welcomeTreatmentContractPage.data });
    }, [welcomeTreatmentContractPage]);

    useEffect(() => {
        if (!accessRequestsPage) return;

        usePrismicStore.setState({ accessRequests: accessRequestsPage.data });
    }, [accessRequestsPage]);

    useEffect(() => {
        if (!medicalRecords) return;

        usePrismicStore.setState({ medicalRecords: medicalRecords.data });
    }, [medicalRecords]);

    useEffect(() => {
        if (!patientsAppConfig) return;

        const featureFlags = patientsAppConfig.results[0]?.data.feature_flags.reduce(
            (acc, { feature_flag }) => ({
                ...acc,
                [feature_flag[0].text]: true,
            }),
            {}
        );

        usePrismicStore.setState({ featureFlags: featureFlags });
    }, [patientsAppConfig]);
};

export default usePrismic;
